import { Operator } from "@/models"
import { Instant, PlainDate } from "@/temporal"
import { toRaw } from "vue"
import { EnumSchema, type ApiSchema, type Component } from "vue-utils"
import { createArrayDisplay } from "./ArrayDisplay"
import BooleanDisplay from "./BooleanDisplay"
import { createEnumDisplay } from "./EnumDisplay"
import TextDisplay from "./TextDisplay"

export interface GenericDisplayProps<T> {
	value: T | null
}

const displayComponents = new Map<ApiSchema<unknown>, Component<GenericDisplayProps<unknown>>>()

function addDisplayComponent<T>(schema: ApiSchema<T>, component: Component<GenericDisplayProps<T>>) {
	displayComponents.set(schema as ApiSchema<unknown>, component as Component<GenericDisplayProps<unknown>>)
}

function getRawDisplayComponent<T>(schema: ApiSchema<T>): Component<GenericDisplayProps<T>> | null {
	const component = displayComponents.get(schema as ApiSchema<unknown>) as Component<GenericDisplayProps<T>>
	if (component) {
		return component
	}
	if (schema instanceof EnumSchema) {
		return createEnumDisplay() as Component<GenericDisplayProps<T>>
	}
	return null
}

export function getDisplayComponent<T>(
	schema: ApiSchema<T>,
	operator?: Operator
): Component<GenericDisplayProps<unknown>> {
	if (typeof schema === "object") {
		schema = toRaw(schema)
	}

	const component = getRawDisplayComponent(schema)
	if (!component) {
		throw new Error("Unknown schema component")
	}
	if (operator !== null) {
		if (operator === Operator.In || operator === Operator.NotIn) {
			return createArrayDisplay<T>(component) as Component<GenericDisplayProps<unknown>>
		}
		if (operator === Operator.Exists || operator === Operator.NotExists) {
			return () => <></>
		}
	}
	return component as Component<GenericDisplayProps<unknown>>
}

export function hasDisplayComponent<T>(schema: ApiSchema<T>): boolean {
	return displayComponents.has(schema as ApiSchema<unknown>)
}

addDisplayComponent(Number, TextDisplay)
addDisplayComponent(String, TextDisplay)
addDisplayComponent(Boolean, BooleanDisplay)

addDisplayComponent(PlainDate.Schema, ({ value }) => value?.toGBString())
addDisplayComponent(Instant.Schema, ({ value }) => value?.toGBDateString())
